import { Component, OnInit, OnDestroy } from "@angular/core";
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../../utils/froala.utils';
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { AuthService } from "../../../../services/auth.service";
import { LoaderService } from "../../../services/loader.service";
import { NoteService } from "../../../services/note.service";

import { genId } from "../../../../core/services/genId";
import { Note } from "../../../models/note.model";
import { NoteRelatedItemType } from "../../../models/note_relatedItemType.enum";
import { NoteType } from "../../../models/note-type.enum";
import { RetentionPolicy } from "../../../models/retention-policy.model";
import { CriticalNoteService } from "../../../services/critical-note.service";
import { SidePanelBase } from "../../side-panel-base.class";

@Component({
    selector: 'organization-notes',
    templateUrl: './organization-notes.component.html',
    styleUrls: ['../../notes/notes.component.scss'],
    standalone: false
})
export class OrganizationNotesComponent extends SidePanelBase implements OnInit, OnDestroy {
    title: string = 'Add Organization Notes';


    subscription: Subscription = new Subscription();

    constructor(
        public authService: AuthService,
        public loaderService: LoaderService,
        public noteService: NoteService,
        private criticalNoteService: CriticalNoteService
    ) { super(); }

    static componentName = 'OrganizationNotesComponent';

    form?: FormGroup;

    private _froalaOptions: any = null;
    get froalaOptions(): any {
        if (!this._froalaOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: key,
                typingTimer: 150,
                saveInterval: 0,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough']
            });
        }
        return this._froalaOptions;
    };

    noteType = NoteType;
   
    _orgNotes: Note[] = [];

    types: any[] = ["General", "Internal", "Administrative"];


    ngOnInit() {
        this.initializeForm();
        var combinedIds = this.componentData.data.id;

        if (this.componentData.title)
            this.title = this.componentData.title;

        if (this.componentData.data.historicalOwnerNumber != undefined) {
            combinedIds += ',' + this.componentData.data.historicalOwnerNumber;
        }
        //To start with the notes come back with the org, so we can get the initial set, we do want to refetch them separately afterwards to make sure they're up do date though.
        if (this.componentData.data.notes)
            this._orgNotes = this.componentData.data.notes;

        //Fetch the updated notes tied to the org
        this.noteService.getNotes(combinedIds, this.componentData.data.noteType || NoteRelatedItemType.Organization).subscribe(notes => {
            this._orgNotes = notes;
        });

        let adds$ = this.criticalNoteService.noteAddedObs$.subscribe(
            (note: Note) => {
                let existingIndex = this._orgNotes.findIndex(n => n.id === note.id);
                if (existingIndex === -1) this._orgNotes.unshift(note);
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(adds$);
        let dems$ = this.criticalNoteService.noteDemotedObs$.subscribe(
            (note: Note) => {
                let existingIndex = this._orgNotes.findIndex(n => n.id === note.id);
                if (existingIndex !== -1) {
                    this._orgNotes.splice(existingIndex, 1);
                }
                this._orgNotes.unshift(note);
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(dems$);
        let dels$ = this.criticalNoteService.noteDeletedObs$.subscribe(
            (note: Note) => {
                let existingIndex = this._orgNotes.findIndex(n => n.id === note.id);
                if (existingIndex !== -1) {
                    this._orgNotes.splice(existingIndex, 1);
                }
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(dels$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    cancel(): void {
        this.resetForm();
    }

    openReplyForm(form: any): void {
        form.isReplying = true
        form.replyText = '';
    }

    get organizationNotes() {
        return this._orgNotes;
    }

    reply(parentNote: Note, form: any): void {

        this.loaderService.toggleLoader(true);
        let reply = this.buildNote(form.replyText, parentNote.type);
        reply.text = form.replyText;
        reply.type = parentNote.type;
        reply.parentNoteId = parentNote.id;
        reply.referenceUri = parentNote.referenceUri;
        parentNote.notes.push(reply);

        const index = this._orgNotes.findIndex(x => x.id === parentNote.id);
        form.opened = true;
        this.organizationNotes[index] = parentNote;

        this.noteService.postNote(reply).subscribe(note => {
            this.loaderService.toggleLoader(false);
            this.resetReplyForm(form);
        });
    }

    resetReplyForm(form: any): void {
        form.replyText = '';
        form.isReplying = false;
        form.isDirty = false
    }

    save(): void {

        this.loaderService.toggleLoader(true);
        const note = this.buildNote(stripFroalaWatermark(this.form.controls['noteText'].value), parseInt(this.form.controls['selectedNoteType'].value));
        note.referenceUri = window.location.href;
     

        this.noteService.postNote(note).subscribe(note => {
        this.loaderService.toggleLoader(false);
        this.organizationNotes.unshift(note);
        this.resetForm();
        });
    }

    public buildNote(text: string, type: number): Note {
        return {
            createdBy: this.authService.User.displayName,
            createdById: this.authService.User.id,
            createdDate: new Date(),
            documentType: 'Note',
            relatedItemId: this.componentData.data.id,
            relatedItemType: this.componentData.data.noteType || NoteRelatedItemType.Organization,
            files: [],
            id: genId(),
            isDeleted: false,
            noteId: genId(),
            notes: [],
            priority: 3,
            referenceUri: '',
            retentionPolicy: {} as RetentionPolicy,
            text: text,
            type: type,
            noteUrgency: 0,
        } as Note;
    }

    public initializeForm(): void {

        let formControls = {
            selectedNoteType: new FormControl(-1, [Validators.required, Validators.min(0)]),
            noteText: new FormControl('', [Validators.required])
        };

        this.form = new FormGroup(formControls);
    }

    public resetForm(): void {
        this.form.reset({ selectedNoteType: -1, noteText: '' });
        this.form.markAsPristine();
        this.form.markAsUntouched();
    }

}
